<script setup lang="ts">
import { getThumbnailImage } from "~/utils/common";
import moment from "moment";
import CountdownTimer from "~/components/Video/CountdownTimer.vue";
import useNotifications from "~/composables/useNotifications";
import type { PropType } from "vue";
import { type Collection } from "~/types";
import PgChip from "~/components/Common/PgChip.vue";
import useCollectionAiredData from "~/composables/useCollectionAiredData";

const props = defineProps({
  collection: {
    type: Object as PropType<Collection & { onAir: boolean }>,
    required: true,
  },
});

const {
  notificationsVisible,
  notificationsTooltip,
  notificationsIcon,
  toggleNotifications,
  isNotificationsLoading,
} = useNotifications(props.collection);

const { chipInfo } = useCollectionAiredData(props.collection);
</script>

<template>
  <div class="flex flex-col gap-5 md:flex-row">
    <div
      class="w-full flex-shrink-0 rounded-lg border border-surface-border p-3 md:w-96 md:p-5"
    >
      <NuxtImg
        alt="main thumbnail"
        class="aspect-video w-full"
        :src="
          getThumbnailImage([
            collection?.images || [],
            collection?.pre_air_images || [],
          ])
        "
        placeholder="/images/placeholder.png"
        loading="lazy"
      />

      <CountdownTimer
        v-if="!collection.onAir"
        :show-header="false"
        :fluid="true"
        class="relative mt-5 !h-fit bg-transparent"
        :air-date="new Date(collection.aired_at)"
        :video-type="collection.type"
      />
    </div>

    <div class="flex flex-col justify-center">
      <div>
        <PgChip
          v-if="chipInfo.label"
          :icon="chipInfo.icon"
          :label="chipInfo.label"
          :bg="chipInfo.bg"
          class="mb-5"
        />
      </div>

      <h1 class="mb-2 text-xl font-bold">
        {{ collection?.title }}
      </h1>
      <h1
        v-if="collection?.subheading?.trim().length"
        class="mb-2 font-bold text-primary-200 lg:text-sm"
      >
        {{ collection?.subheading }}
      </h1>
      <div v-if="collection?.description" class="mb-5 text-xs text-surface-300">
        {{ collection?.description }}
      </div>
      <div class="flex items-center">
        <div>
          <font-awesome-icon :icon="['far', 'calendar']" class="mr-2" />
          <span class="font-semibold">
            {{ moment(collection.aired_at).format("dddd, MMMM D") }}
          </span>
        </div>
        <Divider layout="vertical" />
        <div>
          <font-awesome-icon :icon="['fas', 'clock']" class="mr-2" />
          <span class="font-semibold">
            {{ moment(collection.aired_at).format("hh:mm A") }}
          </span>
        </div>
      </div>
      <div v-if="notificationsVisible" class="mt-5">
        <PgIcon
          v-tooltip="notificationsTooltip"
          :icon="notificationsIcon"
          :loading="isNotificationsLoading"
          @click="toggleNotifications"
        />
      </div>
    </div>
  </div>
</template>

<style scoped></style>
